import React, { useState, useEffect, useCallback, useRef } from 'react';
import { saleService } from '../api/saleService';
import Pagination from '../components/Common/Pagination';
import './Sales.css';

function Sales() {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [selectedSale, setSelectedSale] = useState(null);
  const [showSaleModal, setShowSaleModal] = useState(false);
  const [loadingSale, setLoadingSale] = useState(false);

  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState({
    source: '',
    date_from: '',
    date_to: '',
    search: '',
    per_page: 15,
  });

  // Debounce search — only update filters.search after 350ms of no typing
  const searchTimer = useRef(null);
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: value }));
      setCurrentPage(1);
    }, 350);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchInput('');
    setFilters({ source: '', date_from: '', date_to: '', search: '', per_page: 15 });
    setCurrentPage(1);
  };

  const fetchSales = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters, page: currentPage };
      // Include the full end of day for date_to so the last day's records are included
      if (params.date_to) params.date_to = params.date_to + ' 23:59:59';
      const response = await saleService.getSales(params);
      setSales(response.data || []);
      setLastPage(response.last_page || 1);
      setTotal(response.total || 0);
      // Sum total revenue from current page for display — if backend provides it use it
      if (response.total_revenue !== undefined) {
        setTotalRevenue(response.total_revenue);
      }
    } catch (error) {
      console.error('Error fetching sales:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters]);

  useEffect(() => {
    fetchSales();
  }, [fetchSales]);

  const handleViewSale = async (sale) => {
    setSelectedSale(sale); // show immediately with list data
    setShowSaleModal(true);
    setLoadingSale(true);
    try {
      const data = await saleService.getSale(sale.id);
      setSelectedSale(data);
    } catch (error) {
      console.error('Error fetching sale details:', error);
      // keep the list-level data already set
    } finally {
      setLoadingSale(false);
    }
  };

  const closeModal = () => {
    setShowSaleModal(false);
    setSelectedSale(null);
  };

  const handleDownloadInvoice = async (saleId, e) => {
    e.stopPropagation();
    try {
      const blob = await saleService.downloadInvoice(saleId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `facture_vente_${String(saleId).padStart(6, '0')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading invoice:', error);
      alert('Échec du téléchargement de la facture');
    }
  };

  // Normalise sale items — backend serialises camelCase to snake_case
  const getSaleItems = (sale) => sale?.sale_items || sale?.saleItems || [];

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const formatAmount = (amount) =>
    parseFloat(amount || 0).toFixed(2) + ' DH';

  const hasActiveFilters = filters.source || filters.date_from || filters.date_to || filters.search;

  return (
    <div className="sales-container">
      {/* Header */}
      <div className="sales-header">
        <div>
          <h1>Ventes</h1>
          <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '14px' }}>
            Toutes les ventes — site web et point de vente
          </p>
        </div>
        <div className="header-stats">
          <div className="stat-item">
            <span className="stat-label">Total ventes</span>
            <span className="stat-value">{total}</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="sales-filters">
        <div className="filter-row">
          {/* Search — debounced */}
          <input
            type="text"
            value={searchInput}
            onChange={handleSearchChange}
            placeholder="Rechercher par client, email, facture…"
            className="filter-input search-input"
          />

          <select
            name="source"
            value={filters.source}
            onChange={handleFilterChange}
            className="filter-select"
          >
            <option value="">Toutes les sources</option>
            <option value="store">Magasin (POS)</option>
            <option value="website">Site web</option>
          </select>

          <input
            type="date"
            name="date_from"
            value={filters.date_from}
            onChange={handleFilterChange}
            className="filter-input"
            title="Du"
          />

          <input
            type="date"
            name="date_to"
            value={filters.date_to}
            onChange={handleFilterChange}
            className="filter-input"
            title="Au"
          />

          <select
            name="per_page"
            value={filters.per_page}
            onChange={handleFilterChange}
            className="filter-select"
          >
            <option value={15}>15 / page</option>
            <option value={25}>25 / page</option>
            <option value={50}>50 / page</option>
          </select>

          {hasActiveFilters && (
            <button onClick={clearFilters} className="btn-clear-filters">
              Effacer
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="loading-message">Chargement des ventes…</div>
      ) : sales.length === 0 ? (
        <div className="empty-message">
          Aucune vente trouvée{hasActiveFilters ? ' pour ces filtres' : ''}
        </div>
      ) : (
        <div className="sales-table-container">
          <table className="sales-table">
            <thead>
              <tr>
                <th>Vente #</th>
                <th>Facture</th>
                <th>Date</th>
                <th>Client</th>
                <th>Source</th>
                <th>Articles</th>
                <th>Total</th>
                <th>Paiement</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sales.map((sale) => (
                <tr
                  key={sale.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleViewSale(sale)}
                >
                  <td className="order-number">
                    #{String(sale.id).padStart(6, '0')}
                  </td>
                  <td className="order-number" style={{ fontSize: '12px' }}>
                    {sale.invoice_uuid
                      ? sale.invoice_uuid.substring(0, 8) + '…'
                      : '—'}
                  </td>
                  <td className="order-date">
                    <div>{new Date(sale.created_at).toLocaleDateString('fr-FR')}</div>
                    <div className="order-time">
                      {new Date(sale.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </td>
                  <td className="client-info">
                    {sale.client ? (
                      <>
                        <div className="client-name">
                          {sale.client.first_name} {sale.client.last_name}
                        </div>
                        <div className="client-contact">{sale.client.email}</div>
                      </>
                    ) : (
                      <>
                        <div className="client-name">{sale.guest_name || 'Client de passage'}</div>
                        {sale.guest_email && (
                          <div className="client-contact">{sale.guest_email}</div>
                        )}
                      </>
                    )}
                  </td>
                  <td>
                    <span className={`badge badge-${sale.source}`}>
                      {sale.source === 'store' ? 'Magasin' : 'Site web'}
                    </span>
                  </td>
                  <td className="items-count">
                    {sale.sale_items_count ?? getSaleItems(sale).length}
                  </td>
                  <td className="order-total">{formatAmount(sale.total)}</td>
                  <td>
                    {sale.payment_method && (
                      <div className="payment-method" style={{ textTransform: 'capitalize' }}>
                        {sale.payment_method}
                      </div>
                    )}
                  </td>
                  <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => handleViewSale(sale)}
                      className="btn-view-sale"
                      style={{ marginRight: '6px' }}
                    >
                      Voir
                    </button>
                    <button
                      onClick={(e) => handleDownloadInvoice(sale.id, e)}
                      className="btn-download-invoice"
                    >
                      Facture
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination + info */}
      {!loading && total > 0 && (
        <>
          <Pagination
            currentPage={currentPage}
            lastPage={lastPage}
            onPageChange={setCurrentPage}
          />
          <p style={{ textAlign: 'center', fontSize: '13px', color: '#6b7280', margin: '8px 0 0' }}>
            Page {currentPage} sur {lastPage} — {total} vente{total !== 1 ? 's' : ''}
          </p>
        </>
      )}

      {/* Sale Detail Modal */}
      {showSaleModal && (
        <div
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: '16px',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div style={{
            backgroundColor: '#fff', borderRadius: '12px', padding: '28px',
            width: '100%', maxWidth: '660px', maxHeight: '88vh', overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          }}>
            {/* Modal header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700' }}>
                  {selectedSale
                    ? `Vente #${String(selectedSale.id).padStart(6, '0')}`
                    : 'Détails de la vente'}
                </h2>
                {selectedSale?.invoice_uuid && (
                  <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#6b7280', fontFamily: 'monospace' }}>
                    {selectedSale.invoice_uuid}
                  </p>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {selectedSale && (
                  <button
                    onClick={(e) => handleDownloadInvoice(selectedSale.id, e)}
                    style={{ padding: '8px 14px', background: '#dc143c', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}
                  >
                    Facture
                  </button>
                )}
                <button
                  onClick={closeModal}
                  style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#718096', lineHeight: 1, padding: '0 4px' }}
                >
                  ×
                </button>
              </div>
            </div>

            {loadingSale ? (
              <div style={{ textAlign: 'center', padding: '48px', color: '#718096' }}>
                Chargement…
              </div>
            ) : selectedSale ? (
              <div>
                {/* Info grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px', marginBottom: '20px' }}>
                  <div style={infoBox}>
                    <div style={infoLabel}>Date</div>
                    <div style={infoValue}>{formatDate(selectedSale.created_at)}</div>
                  </div>
                  <div style={infoBox}>
                    <div style={infoLabel}>Source</div>
                    <span className={`badge badge-${selectedSale.source}`}>
                      {selectedSale.source === 'store' ? 'Magasin' : 'Site web'}
                    </span>
                  </div>
                  {selectedSale.payment_method && (
                    <div style={infoBox}>
                      <div style={infoLabel}>Paiement</div>
                      <div style={{ ...infoValue, textTransform: 'capitalize' }}>{selectedSale.payment_method}</div>
                    </div>
                  )}
                  {selectedSale.discount_amount > 0 && (
                    <div style={infoBox}>
                      <div style={infoLabel}>Remise</div>
                      <div style={{ ...infoValue, color: '#e53e3e' }}>−{formatAmount(selectedSale.discount_amount)}</div>
                    </div>
                  )}
                </div>

                {/* Client */}
                <div style={sectionBox}>
                  <div style={sectionTitle}>Client</div>
                  {selectedSale.client ? (
                    <>
                      <div style={{ fontWeight: '600', marginBottom: '2px' }}>
                        {selectedSale.client.first_name} {selectedSale.client.last_name}
                      </div>
                      {selectedSale.client.email && <div style={{ color: '#718096', fontSize: '13px' }}>{selectedSale.client.email}</div>}
                      {selectedSale.client.phone && <div style={{ color: '#718096', fontSize: '13px' }}>{selectedSale.client.phone}</div>}
                    </>
                  ) : (
                    <>
                      <div style={{ fontWeight: '600', marginBottom: '2px' }}>{selectedSale.guest_name || 'Client de passage'}</div>
                      {selectedSale.guest_email && <div style={{ color: '#718096', fontSize: '13px' }}>{selectedSale.guest_email}</div>}
                      {selectedSale.guest_phone && <div style={{ color: '#718096', fontSize: '13px' }}>{selectedSale.guest_phone}</div>}
                    </>
                  )}
                </div>

                {/* Items */}
                {getSaleItems(selectedSale).length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <div style={sectionTitle}>Articles</div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                          <th style={{ textAlign: 'left', padding: '8px 4px', color: '#4a5568', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase' }}>Produit</th>
                          <th style={{ textAlign: 'center', padding: '8px', color: '#4a5568', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase', width: '50px' }}>Qté</th>
                          <th style={{ textAlign: 'right', padding: '8px 4px', color: '#4a5568', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase', width: '90px' }}>P.U.</th>
                          <th style={{ textAlign: 'right', padding: '8px 4px', color: '#4a5568', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase', width: '90px' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getSaleItems(selectedSale).map((item, idx) => {
                          const name = item.product?.name || item.promo_pack?.name || item.product_name || `Article #${idx + 1}`;
                          const sku = item.product?.sku;
                          const unitPrice = parseFloat(item.unit_price || 0);
                          const lineTotal = unitPrice * (item.quantity || 1);
                          return (
                            <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                              <td style={{ padding: '10px 4px' }}>
                                <div>{name}</div>
                                {sku && <div style={{ fontSize: '11px', color: '#a0aec0' }}>SKU: {sku}</div>}
                                {item.warranty && (
                                  <div style={{ fontSize: '11px', color: '#38a169', marginTop: '2px' }}>
                                    Garantie : {item.warranty.uuid?.substring(0, 8)}…
                                  </div>
                                )}
                              </td>
                              <td style={{ textAlign: 'center', padding: '10px 8px' }}>{item.quantity}</td>
                              <td style={{ textAlign: 'right', padding: '10px 4px' }}>{unitPrice.toFixed(2)} DH</td>
                              <td style={{ textAlign: 'right', padding: '10px 4px', fontWeight: '600' }}>
                                {lineTotal.toFixed(2)} DH
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Total */}
                <div style={{ borderTop: '2px solid #e2e8f0', paddingTop: '12px' }}>
                  {selectedSale.discount_amount > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#718096', marginBottom: '4px' }}>
                      <span>Remise</span>
                      <span style={{ color: '#e53e3e' }}>−{formatAmount(selectedSale.discount_amount)}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '20px' }}>
                    <span style={{ fontSize: '15px', color: '#4a5568', fontWeight: '500' }}>Total</span>
                    <span style={{ fontSize: '24px', fontWeight: '800', color: '#2d3748' }}>
                      {formatAmount(selectedSale.total)}
                    </span>
                  </div>
                </div>

                {/* Notes */}
                {selectedSale.notes && (
                  <div style={{ marginTop: '16px', padding: '12px', background: '#fffbeb', borderRadius: '6px', border: '1px solid #fde68a' }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#92400e', marginBottom: '4px' }}>Notes</div>
                    <div style={{ fontSize: '14px', color: '#78350f' }}>{selectedSale.notes}</div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

// Inline style helpers
const infoBox = {
  padding: '10px 12px', backgroundColor: '#f7fafc', borderRadius: '8px',
};
const infoLabel = {
  fontSize: '11px', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px', fontWeight: '600',
};
const infoValue = {
  fontWeight: '600', fontSize: '14px',
};
const sectionBox = {
  padding: '14px', border: '1px solid #e2e8f0', borderRadius: '8px', marginBottom: '16px',
};
const sectionTitle = {
  fontSize: '12px', fontWeight: '700', color: '#4a5568', marginBottom: '10px',
  textTransform: 'uppercase', letterSpacing: '0.05em',
};

export default Sales;
